import debounce from 'debounce';

const emailMatcher = /^(?<localPart>[^\s@]+)@(?<domain>[^\s@]+\.[^\s@]+)$/;

export function urlPath(...components: string[]) {
  return components
    .map((component) =>
      // If we escape “@me” to “%40me”, bad things happen...
      component === '@me' ? component : encodeURIComponent(component),
    )
    .join('/');
}

export function checkEmail(text: unknown) {
  if (typeof text !== 'string') {
    return false;
  }
  return (
    (text.match(emailMatcher)?.groups as unknown as {
      localPart: string;
      domain: string;
    }) || false
  );
}

export function delayForSeconds(
  seconds: number,
  abortSignal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, seconds * 1000);
    abortSignal?.addEventListener?.('abort', () => {
      clearTimeout(timeout);
      reject(abortSignal?.reason);
    });
  });
}

export function makeDebouncedAsyncValidator<
  ValidatorArgs extends unknown[],
  ValidatorResult,
>(
  asyncValidator: (...args: ValidatorArgs) => Promise<ValidatorResult>,
  debounceTimeMilliseconds: number,
) {
  let resultResolver:
    | ((value: ValidatorResult | Promise<ValidatorResult>) => void)
    | null = null;
  let resultPromise: Promise<ValidatorResult> | null = null;

  const runAsyncValidatorDebounced = debounce((...args: ValidatorArgs) => {
    resultResolver?.(asyncValidator(...args));
    resultResolver = null;
    resultPromise = null;
  }, debounceTimeMilliseconds);

  return (...args: ValidatorArgs) => {
    if (!resultPromise) {
      // The first time the debounced validator is called, a
      // promise is created that will eventually resolve with the
      // result of running asyncValidator with the latest args.
      // Successive rapid calls to this function return that
      // promise, until eventually asyncValidator executes,
      // resolves the promise for all previous callers, and clears
      // it to begin again on the next call.
      resultPromise = new Promise((resolve) => {
        // To achieve the desired result, we have to save the resolver
        // supplied to us by the promise constructor, to be used
        // later by the debounced check.
        resultResolver = resolve;
      });
    }
    runAsyncValidatorDebounced(...args);
    return resultPromise;
  };
}

export function flattenFormattedParts(
  parts: string | unknown | Array<string | unknown>,
): string | unknown | Array<string | unknown> {
  if (!Array.isArray(parts)) {
    return parts;
  }
  const flattenedInnerParts = parts.map((part) => flattenFormattedParts(part));
  if (flattenedInnerParts.every((part) => typeof part === 'string')) {
    return flattenedInnerParts.join('');
  }
  return flattenedInnerParts;
}
